import type { HTMLAttributes } from 'react';
import { useEffect, useRef } from 'react';
import { getNumberFromIntlString } from '@elfsight/widget-utils';
import { ANIMATION_SPEED_MS } from '../../constants';
import { formatNumberByLanguage } from '../utils';

export function AnimateDataAttribute({
  'data-count': dataCount,
  ...forwardingProps
}: Omit<AnimateNumberProps, 'value' | 'get' | 'set'> & {
  'data-count': string;
}) {
  return (
    <AnimateNumber
      {...forwardingProps}
      value={dataCount}
      get={(ref) => ref.getAttribute('data-count')!}
      set={(ref, value) => ref.setAttribute('data-count', value)}
    />
  );
}

export function AnimateChildren({
  children,
  ...forwardingProps
}: Omit<AnimateNumberProps, 'value' | 'get' | 'set'> & {
  children: number | string;
}) {
  return (
    <AnimateNumber
      {...forwardingProps}
      value={children}
      get={(ref) => ref.innerText}
      set={(ref, value) => (ref.innerText = value)}
    />
  );
}

type AnimateNumberProps = HTMLAttributes<HTMLDivElement> & {
  value: string | number;
  language: string | undefined;
  showVotes: boolean;
  animateSlotStyle?: {
    lineHeight: string;
    fontSize: string;
    fontWeight: string;
  };
  get(ref: HTMLDivElement): string;
  set(ref: HTMLDivElement, value: string): void;
  onAnimationEnd(): void;
  onAnimateSlotWidthChange?(width: number): void;
};

function AnimateNumber({
  value,
  language,
  showVotes,
  animateSlotStyle,
  get,
  set,
  onAnimationEnd,
  onAnimateSlotWidthChange,
  ...forwardingProps
}: AnimateNumberProps) {
  const numberedValue =
    typeof value === 'number'
      ? value
      : getNumberFromIntlString(value, language);

  const animateRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const { current: animate } = animateRef;

    if (!animate) {
      return;
    }

    const startValue = get(animate)
      ? getNumberFromIntlString(get(animate), language)
      : 0;

    const range = numberedValue - startValue;
    const startTime = performance.now();

    const updateCount = (timestamp: number) => {
      const elapsedTime = timestamp - startTime;
      const progress = Math.min(elapsedTime / ANIMATION_SPEED_MS, 1);
      const currentValue = Math.max(0, startValue + range * progress);

      set(animate, formatNumberByLanguage(Math.round(currentValue), language));

      if (progress < 1) {
        requestAnimationFrame(updateCount);
      } else {
        onAnimationEnd();
        set(animate, formatNumberByLanguage(numberedValue, language));
      }
    };

    const request = requestAnimationFrame(updateCount);

    return () => cancelAnimationFrame(request);
  }, [numberedValue, language, showVotes]);

  useAnimateSlotWidth({
    animateRef,
    numberedValue,
    animateSlotStyle,
    onAnimateSlotWidthChange
  });

  return <div ref={animateRef} {...forwardingProps} />;
}

function useAnimateSlotWidth({
  animateRef,
  numberedValue,
  animateSlotStyle,
  onAnimateSlotWidthChange
}: {
  animateRef: React.RefObject<HTMLDivElement>;
  numberedValue: number;
  animateSlotStyle?: AnimateNumberProps['animateSlotStyle'];
  onAnimateSlotWidthChange?: AnimateNumberProps['onAnimateSlotWidthChange'];
}) {
  useEffect(() => {
    const { current: animate } = animateRef;

    if (!onAnimateSlotWidthChange || !animate || !animateSlotStyle) {
      return;
    }

    const tempSpan = document.createElement('span');

    tempSpan.style.visibility = 'hidden';
    tempSpan.style.position = 'absolute';
    tempSpan.style.lineHeight = animateSlotStyle.lineHeight;
    tempSpan.style.fontSize = animateSlotStyle.fontSize;
    tempSpan.style.fontWeight = animateSlotStyle.fontWeight;
    tempSpan.textContent = String(numberedValue);

    animate.appendChild(tempSpan);

    /**
     * offsetWidth is rounded value, so we need to add 1 to get the real value (ex: 7.34 vs 7)
     * if we don't add extra 1, it will create a jumping effect
     */
    const roundedValue = tempSpan.offsetWidth + 1;
    onAnimateSlotWidthChange(roundedValue);

    animate.removeChild(tempSpan);
  }, [numberedValue]);
}
